/**
 * feat-230 — API client for admin CRUD of trivia sets and their questions
 * (set → question; two levels, unlike the quiz curriculum's three).
 *
 * Backend: routes/admin_trivia.py, mounted under `/api` alongside the other
 * admin routers and gated by `require_corporate_manager_or_admin` with a
 * tenant-authorization check before every write (spec §API).
 *
 * NOTE ON SPANISH: nothing here collects Spanish. Per spec §Translation, an
 * admin write fires `retranslate()` as a BackgroundTask on the backend and the
 * Spanish rows land in the existing `/admin/translations` review queue. The
 * write bodies therefore ALWAYS include every translatable field
 * (`title`; `question_text`, `option_a..d`, `reveal_text`) so retranslation
 * fires with the full picture — same rule as `QuestionDetailForm`'s save.
 *
 * No try/catch in this module: `fetchApi` throws on a non-ok response and the
 * calling component catches and renders the message.
 */

import { config } from '../config/environment';
import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

// ============================================================
// Types
// ============================================================

/**
 * Which option is correct, as a LETTER — not an index. `option_c`/`option_d`
 * are optional, so a letter can name a blank option; that combination is a bug
 * the Flutter pop-up cannot render (no tile to highlight). The authoring UI is
 * the guard: the correct-answer radio is disabled on a blank option and the
 * selection is cleared if the marked option is emptied. See
 * `design/admin-page-notes.md` §5 and the Flutter brief §5.3 — both ends have to
 * agree or the bug is silent on both sides.
 */
export type TriviaCorrectOption = 'a' | 'b' | 'c' | 'd';

export interface TriviaQuestion {
  id: string;
  trivia_set_id?: string;
  question_text: string;
  /** Required — every question has at least two options. */
  option_a: string;
  option_b: string;
  /** Optional. NULL/empty ⇒ the app renders a 2- or 3-option question. */
  option_c: string | null;
  option_d: string | null;
  correct_option: TriviaCorrectOption;
  /** The fun-fact text revealed after the server answers. */
  reveal_text: string;
  sort_order: number;
}

export interface TriviaSet {
  id: string;
  tenant_id: string;
  title: string;
  /** `YYYY-MM-DD`, inclusive. May equal `end_date` (the common one-set-per-day case). */
  start_date: string;
  /** `YYYY-MM-DD`, inclusive. Never earlier than `start_date`. */
  end_date: string;
  created_at?: string;
  updated_at?: string;
  questions: TriviaQuestion[];
}

export type TriviaSetWrite = {
  title: string;
  start_date: string;
  end_date: string;
};

export type TriviaQuestionWrite = {
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string | null;
  option_d: string | null;
  correct_option: TriviaCorrectOption;
  reveal_text: string;
  sort_order: number;
};

// ============================================================
// Date helpers (shared with the editor so the rule lives in one place)
// ============================================================

/** Today as `YYYY-MM-DD` in the browser's local zone — the default for a new set. */
export function todayIsoDate(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The one date rule: `end_date` may not precede `start_date`. Equality is
 * VALID and is the common case (a single-day set). Compared as `YYYY-MM-DD`
 * strings, which sort lexicographically — no Date parsing, so no timezone
 * shift. Eligibility itself is evaluated server-side in the server's location
 * timezone (spec §How eligibility is decided); this is only an authoring guard.
 */
export function isValidDateRange(startDate: string, endDate: string): boolean {
  if (!startDate || !endDate) return false;
  return endDate >= startDate;
}

// ============================================================
// API — sets
// ============================================================

export async function listTriviaSets(tenantId: string): Promise<TriviaSet[]> {
  if (isFakeMode()) return fakeListSets(tenantId);
  return fetchApi<TriviaSet[]>(
    `/api/admin/trivia/sets?tenant_id=${encodeURIComponent(tenantId)}`,
    { headers: getAuthHeaders() },
  );
}

export async function createTriviaSet(
  tenantId: string,
  data: TriviaSetWrite,
): Promise<{ id: string }> {
  if (isFakeMode()) return fakeCreateSet(tenantId, data);
  return fetchApi<{ id: string }>('/api/admin/trivia/sets', {
    method: 'POST',
    headers: getAuthHeaders(),
    body: { tenant_id: tenantId, ...data },
  });
}

export async function updateTriviaSet(setId: string, data: TriviaSetWrite): Promise<void> {
  if (isFakeMode()) return fakeUpdateSet(setId, data);
  await fetchApi(`/api/admin/trivia/sets/${setId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deleteTriviaSet(setId: string): Promise<void> {
  if (isFakeMode()) return fakeDeleteSet(setId);
  await fetchApi(`/api/admin/trivia/sets/${setId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

// ============================================================
// API — questions
// ============================================================

export async function createTriviaQuestion(
  setId: string,
  data: TriviaQuestionWrite,
): Promise<{ id: string }> {
  if (isFakeMode()) return fakeCreateQuestion(setId, data);
  return fetchApi<{ id: string }>(`/api/admin/trivia/sets/${setId}/questions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

/**
 * Partial update. Reorder sends `{sort_order}` alone; the detail form sends the
 * full translatable body so retranslation fires with every field present.
 */
export async function updateTriviaQuestion(
  questionId: string,
  data: Partial<TriviaQuestionWrite>,
): Promise<void> {
  if (isFakeMode()) return fakeUpdateQuestion(questionId, data);
  await fetchApi(`/api/admin/trivia/questions/${questionId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deleteTriviaQuestion(questionId: string): Promise<void> {
  if (isFakeMode()) return fakeDeleteQuestion(questionId);
  await fetchApi(`/api/admin/trivia/questions/${questionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

// ============================================================
// `fake` mode — in-memory store, no backend
// ============================================================
//
// `npm run start:fake` must load every view with no backend running
// (interviewer-frontend/CLAUDE.md § Environment System, and the role rule that
// a new view ships with dummy data). The trivia admin API does not exist yet,
// so without this the page is an error banner in every environment a dev can
// actually run today.
//
// Scoped hard to `config.environment === 'fake'` — the same discriminator
// `LessonImageControl` uses (QuizContentManagement.tsx:2058). In local/dev/prod
// every call above returns before touching any of this. State is module-level
// and therefore per-tab-session: a reload resets it, which is the right
// behaviour for a demo surface.

function isFakeMode(): boolean {
  return config.environment === 'fake';
}

let fakeSeq = 0;
const fakeId = (prefix: string) => `${prefix}-fake-${++fakeSeq}`;

let fakeSets: TriviaSet[] | null = null;

function fakeSeed(tenantId: string): TriviaSet[] {
  const today = todayIsoDate();
  return [
    {
      id: 'set-fake-seed-1',
      tenant_id: tenantId,
      title: 'How it all started',
      start_date: today,
      end_date: today,
      questions: [
        {
          id: 'q-fake-seed-1',
          trivia_set_id: 'set-fake-seed-1',
          question_text: 'What year did the very first store open?',
          option_a: '2009',
          option_b: '2013',
          option_c: '2016',
          option_d: null,
          correct_option: 'b',
          reveal_text:
            'It opened in 2013 with nine tables, and the soup pot came from Steve’s own kitchen.',
          sort_order: 1,
        },
        {
          id: 'q-fake-seed-2',
          trivia_set_id: 'set-fake-seed-1',
          question_text: 'Roughly how many gallons of soup do we make in a week?',
          option_a: '40',
          option_b: '400',
          option_c: null,
          option_d: null,
          correct_option: 'b',
          reveal_text: 'About 400 gallons — enough to fill a small hot tub. Please do not.',
          sort_order: 2,
        },
      ],
    },
  ];
}

/** Reset the in-memory `fake` store. Test-only helper; a no-op story elsewhere. */
export function __resetFakeTriviaStore(): void {
  fakeSets = null;
  fakeSeq = 0;
}

function fakeStore(tenantId: string): TriviaSet[] {
  if (fakeSets === null) fakeSets = fakeSeed(tenantId);
  return fakeSets;
}

function fakeFindSet(setId: string): TriviaSet {
  const found = (fakeSets ?? []).find((s) => s.id === setId);
  if (!found) throw new Error(`Trivia set ${setId} not found`);
  return found;
}

async function fakeListSets(tenantId: string): Promise<TriviaSet[]> {
  const sets = fakeStore(tenantId).filter((s) => s.tenant_id === tenantId);
  // Deep copy so a component can never mutate the store by editing its props.
  return JSON.parse(JSON.stringify(sets)) as TriviaSet[];
}

async function fakeCreateSet(tenantId: string, data: TriviaSetWrite): Promise<{ id: string }> {
  const id = fakeId('set');
  fakeStore(tenantId).push({ id, tenant_id: tenantId, ...data, questions: [] });
  return { id };
}

async function fakeUpdateSet(setId: string, data: TriviaSetWrite): Promise<void> {
  Object.assign(fakeFindSet(setId), data);
}

async function fakeDeleteSet(setId: string): Promise<void> {
  fakeSets = (fakeSets ?? []).filter((s) => s.id !== setId);
}

async function fakeCreateQuestion(
  setId: string,
  data: TriviaQuestionWrite,
): Promise<{ id: string }> {
  const id = fakeId('q');
  fakeFindSet(setId).questions.push({ id, trivia_set_id: setId, ...data });
  return { id };
}

async function fakeUpdateQuestion(
  questionId: string,
  data: Partial<TriviaQuestionWrite>,
): Promise<void> {
  for (const set of fakeSets ?? []) {
    const q = set.questions.find((x) => x.id === questionId);
    if (q) {
      Object.assign(q, data);
      set.questions.sort((a, b) => a.sort_order - b.sort_order);
      return;
    }
  }
  throw new Error(`Trivia question ${questionId} not found`);
}

async function fakeDeleteQuestion(questionId: string): Promise<void> {
  for (const set of fakeSets ?? []) {
    const before = set.questions.length;
    set.questions = set.questions.filter((q) => q.id !== questionId);
    if (set.questions.length !== before) return;
  }
}
